import { Button } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useTypedParams } from 'react-router-typesafe-routes/dom';
import { AppRoutes, useNavBack } from '~/app/app.routes';
import { AuthService } from '~/service/service.auth';
import { BrandService } from '~/service/service.brand';
import { UIService } from '~/service/service.ui';
import { BrandCreateDto } from '~/shared/dto/admin/brand.dto';
import { Form } from '~/shared/form/form';
import { useService } from '~/shared/service/service.base';
import { FieldImages } from '~/view/fields/field.images';
import { FieldNumber } from '~/view/fields/field.number';
import { FieldSwitch } from '~/view/fields/field.switch';
import { FieldText } from '~/view/fields/field.text';
import { FieldTextArray } from '~/view/fields/field.textarray';
import { EditCenter, EditContainer, EditLeft, EditRight } from '~/view/view.edit';

export const BrandEditPage = observer(() => {
	const [ui, auth, brands] = useService(UIService, AuthService, BrandService);
	const navBack = useNavBack();
	const { id } = useTypedParams(AppRoutes.StaffEdit);

	const form = Form.use(BrandCreateDto, {
		name: 'BrandCreateDto',
		onSubmit: dto =>
			brands.save(id, dto).then(() => {
				navBack();
				ui.notify('Brand saved successfully', 'success');
			}),
		initial: () => brands.getOne(id),
	});

	ui.usePageTitle(form.initial?.name ? `Brand - ${form.initial?.name}` : '');
	const canSave = id ? auth.can('brand', 'UPDATE') : auth.can('brand', 'CREATE');

	return (
		<EditContainer async={brands.async}>
			<EditLeft>
				<FieldImages field={form.$images} images={form.initial?.images} />
			</EditLeft>
			<EditCenter>
				<FieldNumber disabled={!!id} field={form.$id} />
				<FieldText field={form.$name} />
				<FieldText field={form.$description} multiline />
				<FieldText field={form.$country} />
				<FieldNumber field={form.$year} />
				<FieldTextArray field={form.$synonyms} multiline />
				<FieldText multiline field={form.$comment} />
				<FieldSwitch field={form.$active} />
			</EditCenter>
			<EditRight>
				<Button variant="contained" onClick={form.submit} disabled={!canSave}>
					Save
				</Button>
				<br />
				<Button variant="outlined" onClick={navBack}>
					Cancel
				</Button>
				<Button variant="outlined" onClick={form.reset}>
					Reset
				</Button>
				{id && (
					<>
						<Button variant="outlined" onClick={() => ui.openHistory('BRAND', id)}>
							Show history
						</Button>
						<br />
						<Button
							disabled={!auth.can('brand', 'DELETE')}
							variant="outlined"
							color="error"
							onClick={() =>
								ui.confirmDelete(() =>
									brands.delete(id).then(() => {
										navBack();
										ui.notify('Brand deleted successfully', 'success');
									}),
								)
							}
						>
							Delete
						</Button>
					</>
				)}
			</EditRight>
		</EditContainer>
	);
});
